/**
 * /destinations — pure index (plan 2026-08-04-destinations-v1.md §5.5, D15).
 * Sources flow in, destinations flow out: this is where Brain posts what it
 * finds. v1 ships webhook destinations in the Slack message format.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, Send } from 'lucide-react';
import { EmptyState } from '@/components/patterns/empty-state';
import { IconTile } from '@/components/patterns/icon-tile';
import { PageHeader } from '@/components/patterns/page-header';
import { StatusPill, type StatusPillTone } from '@/components/patterns/status-pill';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { type Destination, destinationsApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

function statusTone(status: Destination['status']): StatusPillTone {
  switch (status) {
    case 'active':
      return 'active';
    case 'paused':
      return 'paused';
    case 'error':
      return 'danger';
    case 'disconnected':
      return 'neutral';
  }
}

function scheduleText(d: Destination): string {
  if (!d.schedule || d.schedule.runs.length === 0) return 'No schedule';
  const times = d.schedule.runs.map((r) => r.at).join(', ');
  return `${times} · ${d.schedule.tz}`;
}

export function DestinationCard({ destination: d }: { destination: Destination }) {
  const run = d.lastRun;
  const runText = run
    ? `Last run ${run.status}${run.dryRun ? ' (dry run)' : ''} · ${new Date(run.startedAt).toLocaleString()}`
    : 'Never run';
  return (
    <div className="rounded-xl border border-border p-6">
      <div className="flex items-center gap-4">
        <IconTile tone="neutral">
          <Send className="h-5 w-5" />
        </IconTile>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold">{d.label ?? 'Slack webhook'}</p>
          <p className="truncate font-mono text-[13px] text-faint">
            {d.host ?? '—'} · {scheduleText(d)}
          </p>
        </div>
        <StatusPill tone={statusTone(d.status)}>{d.status}</StatusPill>
        <Link
          to="/destinations/$destinationId"
          params={{ destinationId: d.id }}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
        >
          View
        </Link>
      </div>
      {d.status === 'error' && d.lastError ? (
        <p className="mt-4 border-t border-border-subtle pt-4 text-sm text-warning">
          {d.lastError}
        </p>
      ) : (
        <p className="mt-4 border-t border-border-subtle pt-4 text-[13px] text-faint">{runText}</p>
      )}
    </div>
  );
}

export function DestinationsPage() {
  const { data, isLoading } = useQuery({
    queryKey: qk.destinations,
    queryFn: () => destinationsApi.list(),
  });
  const destinations = data ?? [];

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="Destinations"
          subtitle="Where Brain sends what it finds. Connect a Slack incoming webhook and distilled proposals land in your channel on a schedule."
        />
        <Link
          to="/destinations/add"
          className={cn(buttonVariants({ variant: 'default', size: 'sm' }), 'shrink-0')}
        >
          <Plus className="h-4 w-4" />
          Add destination
        </Link>
      </div>

      {isLoading ? (
        <div className="mt-8 flex flex-col gap-4">
          <Skeleton className="h-[104px] w-full rounded-xl" />
          <Skeleton className="h-[104px] w-full rounded-xl" />
        </div>
      ) : destinations.length === 0 ? (
        <EmptyState
          className="mt-8"
          icon={Send}
          title="No destinations yet"
          body="Sources bring your messages in; destinations send distilled value back out. Add a Slack webhook and Brain will post its proposals to your channel — dry-run first, nothing posts until you say so."
        />
      ) : (
        <div className="mt-8 flex flex-col gap-4">
          {destinations.map((d) => (
            <DestinationCard key={d.id} destination={d} />
          ))}
        </div>
      )}
    </div>
  );
}
